import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,            // <-- added
} from '@mui/material';
import { AutoAwesome as AutoAwesomeIcon, Save as SaveIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Enhanced AutoAddProjectDialog Component
const AutoAddProjectDialog = forwardRef(({
  open,
  onClose,
  collection,
  templates,
  templatesLoading,
  onSave,
  onLoadTemplates,
  onLoadConfig,
  folderPicker
}, ref) => {
  const { t } = useTranslation();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [watchPath, setWatchPath] = useState('');
  const [autoProjectEnabled, setAutoProjectEnabled] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState({
    templateId: null,
    watchPath: '',
    enabled: false
  });

  const primaryColor = '#5757b0';
  const gradientBg = `linear-gradient(135deg, ${primaryColor}, #7777d0)`;

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open && collection) {
      if (templates.length === 0 && !templatesLoading) {
        onLoadTemplates();
      }
      onLoadConfig(collection.id);
    } else if (!open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, collection, templates.length, templatesLoading]);

  const resetForm = () => {
    setSelectedTemplate(null);
    setWatchPath('');
    setAutoProjectEnabled(false);
    setInitialFormValues({
      templateId: null,
      watchPath: '',
      enabled: false
    });
  };

  // Method to update form with loaded config
  const updateFormWithConfig = useCallback((config) => {
    const template = templates.find(t => String(t.id) === String(config.templateId));
    setSelectedTemplate(template || null);
    setWatchPath(config.watchPath || '');
    setAutoProjectEnabled(config.enabled === true);
    
    setInitialFormValues({
      templateId: config.templateId || null,
      watchPath: config.watchPath || '',
      enabled: config.enabled === true
    });
  }, [templates]);

  // Expose method to parent component
  useImperativeHandle(ref, () => ({
    updateFormWithConfig
  }), [updateFormWithConfig]);

  useEffect(() => {
    if (!open || !initialFormValues.templateId) {
      return;
    }
    const template = templates.find(
      (templateItem) => String(templateItem.id) === String(initialFormValues.templateId)
    );
    if (template) {
      setSelectedTemplate(template);
    }
  }, [initialFormValues.templateId, open, templates]);

  // Check if form has changed from initial values
  const hasFormChanged = () => {
    const currentValues = {
      templateId: selectedTemplate?.id || null,
      watchPath: watchPath.trim(),
      enabled: autoProjectEnabled
    };
    
    return (
      currentValues.templateId !== initialFormValues.templateId ||
      currentValues.watchPath !== initialFormValues.watchPath ||
      currentValues.enabled !== initialFormValues.enabled
    );
  };

  // Check if form is valid for saving
  const isFormValidForSave = () => {
    return selectedTemplate && watchPath.trim();
  };

  const handleSave = () => {
    if (collection && isFormValidForSave()) {
      onSave({
        collectionId: collection.id,
        templateId: selectedTemplate.id,
        watchPath: watchPath.trim(),
        enabled: autoProjectEnabled
      });
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
          boxShadow: '0 20px 60px rgba(87, 87, 176, 0.25), 0 8px 32px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        color: 'white',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        background: gradientBg,
        py: 3,
        px: 4,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '18px',
            background: 'rgba(255, 255, 255, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: '2rem', color: 'white' }} />
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            {t('collection.auto_add_project_title', 'Auto Add Project')}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 400 }}>
            {collection?.title}
          </Typography>
        </Box>

        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '60%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 60%)',
            pointerEvents: 'none'
          }}
        />
      </DialogTitle>

      {/* NEW: clear separation line */}
      <Divider sx={{ borderColor: 'rgba(87, 87, 176, 0.12)', mb: 0 }} />

      {/* Increased top padding */}
      <DialogContent sx={{ p: 4, pt: 6 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Enable Switch Section */}
          <Paper sx={{ 
            borderRadius: '16px', 
            background: 'linear-gradient(135deg, #ffffff 0%, #f9faff 100%)',
            border: '2px solid rgba(87, 87, 176, 0.1)',
            boxShadow: '0 4px 16px rgba(87, 87, 176, 0.08)',
            p: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: '12px', 
                  background: autoProjectEnabled 
                    ? gradientBg
                    : 'linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)',
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}>
                  <AutoAwesomeIcon sx={{ 
                    color: autoProjectEnabled ? 'white' : '#999', 
                    fontSize: '1.2rem' 
                  }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {t('collection.enable_auto_project', 'Enable Auto Project Creation')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('collection.enable_auto_description', 'Automatically create projects when new folders are detected')}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {autoProjectEnabled && (
                  <Chip 
                    label={t('common.active', 'Active')}
                    size="small"
                    sx={{ 
                      backgroundColor: '#4caf50',
                      color: 'white',
                      fontSize: '0.7rem',
                      fontWeight: 700
                    }}
                  />
                )}
                <Box
                  onClick={() => setAutoProjectEnabled(!autoProjectEnabled)}
                  sx={{
                    width: 56,
                    height: 28,
                    borderRadius: '16px',
                    background: autoProjectEnabled 
                      ? gradientBg
                      : 'linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: autoProjectEnabled 
                      ? '0 4px 12px rgba(87, 87, 176, 0.3)'
                      : '0 2px 6px rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: 'linear-gradient(145deg, #ffffff 0%, #f8f8f8 100%)',
                      position: 'absolute',
                      top: '3px',
                      left: autoProjectEnabled ? '31px' : '3px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Template Selection */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box sx={{ 
                width: 32, 
                height: 32, 
                borderRadius: '10px', 
                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
              }}>
                <Typography sx={{ color: 'white', fontSize: '0.9rem', fontWeight: 'bold' }}>
                  T
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
                {t('collection.choose_template', 'Choose Template')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', ml: 'auto' }}>
                {selectedTemplate ? '✓ Selected' : `${templates.length} available`}
              </Typography>
            </Box>

            {templatesLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: primaryColor }} />
                <Typography sx={{ ml: 2, color: 'text.secondary' }}>
                  {t('collection.loading_templates', 'Loading templates...')}
                </Typography>
              </Box>
            ) : templates.length === 0 ? (
              <Paper sx={{ 
                p: 4, 
                textAlign: 'center', 
                backgroundColor: 'rgba(255, 152, 0, 0.05)',
                border: '2px dashed rgba(255, 152, 0, 0.2)',
                borderRadius: '16px'
              }}>
                <Typography variant="h6" sx={{ color: 'rgba(255, 152, 0, 0.8)', mb: 1 }}>
                  {t('collection.no_templates_available', 'No templates available')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('collection.create_templates_first', 'Create some templates first to enable auto project creation.')}
                </Typography>
              </Paper>
            ) : (
              <FormControl 
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                    boxShadow: '0 2px 8px rgba(87, 87, 176, 0.12)',
                    border: '1px solid rgba(87, 87, 176, 0.2)',
                    '&:hover': {
                      borderColor: `${primaryColor}60`,
                      boxShadow: '0 4px 12px rgba(87, 87, 176, 0.18)',
                    },
                    '&.Mui-focused': {
                      borderColor: primaryColor,
                      boxShadow: '0 0 0 3px rgba(87, 87, 176, 0.1)',
                    },
                    '& fieldset': {
                      border: 'none',
                    }
                  }
                }}
              >
                <InputLabel sx={{ fontWeight: 600 }}>
                  {t('collection.choose_template', 'Choose a template...')}
                </InputLabel>
                <Select
                  value={selectedTemplate?.id || ''}
                  onChange={(e) => {
                    const template = templates.find(t => t.id === e.target.value);
                    setSelectedTemplate(template);
                  }}
                  disabled={templatesLoading}
                  renderValue={(selectedId) => {
                    const template = templates.find(t => t.id === selectedId);
                    if (!template) return '';
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)'
                          }}
                        >
                          <Typography sx={{ 
                            color: 'white', 
                            fontSize: '0.9rem', 
                            fontWeight: 'bold'
                          }}>
                            T
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {template.template_title || template.name || `Template ${template.id}`}
                          </Typography>
                          {template.version && (
                            <Typography variant="caption" sx={{ 
                              color: 'text.secondary', 
                              fontWeight: 600
                            }}>
                              v{template.version}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  }}
                >
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={template.id} sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(255, 152, 0, 0.25)'
                          }}
                        >
                          <Typography sx={{ 
                            color: 'white', 
                            fontSize: '1rem', 
                            fontWeight: 'bold'
                          }}>
                            T
                          </Typography>
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ 
                            fontWeight: 700, 
                            color: '#333'
                          }}>
                            {template.template_title || template.name || `Template ${template.id}`}
                          </Typography>
                          {template.version && (
                            <Chip 
                              size="small" 
                              label={`v${template.version}`}
                              sx={{ 
                                height: 20, 
                                fontSize: '0.7rem',
                                backgroundColor: '#e8eaff',
                                color: '#5757b0',
                                fontWeight: 700
                              }}
                            />
                          )}
                          {(template.template_description || template.description) && (
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                fontSize: '0.8rem',
                                mt: 0.5
                              }}
                            >
                              {template.template_description || template.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          {/* Watch Path Section */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ 
                width: 32, 
                height: 32, 
                borderRadius: '10px', 
                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
              }}>
                <Typography sx={{ color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }}>
                  📁
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
                {t('collection.watch_folder', 'Monitor Folder')}
              </Typography>
            </Box>

            <Paper sx={{ 
              borderRadius: '16px', 
              background: 'linear-gradient(135deg, #ffffff 0%, #f9faff 100%)',
              border: '2px solid rgba(87, 87, 176, 0.1)',
              boxShadow: '0 4px 16px rgba(87, 87, 176, 0.08)',
              p: 3
            }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', mb: 2 }}>
                <TextField
                  fullWidth
                  label={t('collection.folder_path', 'Folder Path')}
                  value={watchPath}
                  onChange={(e) => setWatchPath(e.target.value)}
                  placeholder={t('collection.path_placeholder', 'e.g., C:\\Projects\\AutoImport')}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      background: 'rgba(255, 255, 255, 0.7)',
                      '& fieldset': {
                        borderColor: `${primaryColor}30`,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                        borderWidth: '2px'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={folderPicker.handleBrowseDirectory}
                  sx={{
                    minWidth: '120px',
                    height: '56px',
                    borderRadius: '12px',
                    background: gradientBg,
                    '&:hover': { 
                      background: `linear-gradient(135deg, #7777d0, ${primaryColor})`
                    }
                  }}
                >
                  {t('common.browse', 'Browse')}
                </Button>
              </Box>
              
              <Typography variant="body2" sx={{ 
                color: 'text.secondary',
                fontSize: '0.85rem',
                fontStyle: 'italic'
              }}>
                {t('collection.watch_description', 'New folders in this path will trigger project creation')}
              </Typography>
            </Paper>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 4, 
        gap: 2, 
        background: 'rgba(87, 87, 176, 0.02)',
        borderTop: '2px solid rgba(87, 87, 176, 0.1)' 
      }}>
        <Button
          onClick={handleClose}
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1.5,
            '&:hover': { 
              backgroundColor: 'rgba(0,0,0,0.05)'
            }
          }}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!hasFormChanged() || !isFormValidForSave()}
          sx={{
            background: hasFormChanged() && isFormValidForSave()
              ? gradientBg
              : undefined,
            fontWeight: 700,
            borderRadius: '12px',
            px: 4,
            py: 1.5,
            boxShadow: hasFormChanged() && isFormValidForSave()
              ? '0 4px 16px rgba(87, 87, 176, 0.3)'
              : undefined,
            '&:hover': hasFormChanged() && isFormValidForSave() ? {
              background: `linear-gradient(135deg, #7777d0, ${primaryColor})`,
              boxShadow: '0 6px 20px rgba(87, 87, 176, 0.4)'
            } : undefined
          }}
          startIcon={<SaveIcon />}
        >
          {hasFormChanged() 
            ? t('collection.save_config', 'Save Configuration')
            : t('collection.no_changes', 'No Changes')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
});

AutoAddProjectDialog.displayName = 'AutoAddProjectDialog';
export default AutoAddProjectDialog;
